/**
 * F-304 — Texas grouped navbar dropdowns
 * Renders route-registry groups as hover/focus dropdown menus with chevrons,
 * matching the UK nav's dropdown pattern (see MarketAwareNavbar Tenants menu).
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TEXAS_NAV_GROUPS, TEXAS_NAV_MENU_GROUPS, TexasNavGroup, getTexasNavItems } from './routeRegistry';

const TexasGroupedSidebar: React.FC = () => {
    const location = useLocation();
    const groups = TEXAS_NAV_MENU_GROUPS;

    return (
        <nav
            className="flex items-center space-x-6"
            data-testid="texas-grouped-sidebar"
            role="navigation"
            aria-label="Texas navigation"
        >
            {groups.map((group) => {
                const items = getTexasNavItems(group);
                if (items.length === 0) return null;
                const groupIsActive = items.some(
                    (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
                );
                const buttonId = `texas-nav-group-${group}-button`;
                return (
                    <div key={group} className="relative group">
                        <button
                            type="button"
                            id={buttonId}
                            className={`flex items-center space-x-1 ${groupIsActive ? 'text-green-600 font-semibold' : 'text-gray-700 hover:text-green-600'} transition-all`}
                        >
                            <span>{TEXAS_NAV_GROUPS[group]}</span>
                            <i
                                className="fas fa-chevron-down text-[10px] opacity-50 group-hover:rotate-180 transition-transform"
                                aria-hidden="true"
                            ></i>
                        </button>
                        <div
                            className="absolute left-0 mt-2 w-60 bg-white border border-gray-100 rounded-xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all z-50 transform translate-y-2 group-hover:translate-y-0 group-focus-within:translate-y-0"
                        >
                            {/* F-307: force a column — App.css `nav ul` sets a
                                horizontal flex row, which let the SB 38 items
                                overflow the white dropdown background. */}
                            <ul role="list" aria-labelledby={buttonId} className="flex flex-col gap-0 list-none">
                                {items.map((item) => {
                                    const isActive = location.pathname === item.path;
                                    return (
                                        <li key={item.path} role="listitem">
                                            <Link
                                                to={item.path}
                                                className={`texas-sidebar-item flex items-center px-4 py-2 text-sm no-underline ${isActive ? 'text-green-700 font-semibold bg-green-50' : 'text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium'}`}
                                                data-testid={`nav-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                                                aria-current={isActive ? 'page' : undefined}
                                            >
                                                <i className={`${item.icon} mr-2 w-4`} aria-hidden="true"></i>
                                                <span>{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                );
            })}
        </nav>
    );
};

export { TexasGroupedSidebar };
export default TexasGroupedSidebar;
